// The outline of a cluster: pure, synchronous and deterministic. The boxes
// of the cluster's groups come in, and the boxes of the groups about them
// that are not in it; out comes a soft outline that hugs the members, the
// "Bubble Sets" of Collins, Penn and Carpendale (2009). A group in two or
// three clusters lies inside the outline of each, so the outlines overlap
// round it.
//
// The outline is the edge of a field sampled on a grid. Each member raises
// the field near its box, each line of a tree that joins the members raises
// it along the line, and each group not in the cluster lowers it near its
// box. Where the field stands above a threshold is inside; marching squares
// trace that edge, which is thinned and drawn as a smooth closed curve.
//
// Two things are held whatever the field: every member's box lies inside
// with a margin, and the outline is one piece. The members' boxes are
// raised outright to their margin, and where the tree's lines alone do not
// join the members they are strengthened, and at last raised outright as a
// narrow corridor.

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Outline {
  // A closed, smoothed SVG path, in the same coordinates as the boxes
  path: string;
  // The outline's extent
  bounds: { left: number; top: number; right: number; bottom: number };
  // The topmost point of the outline, for a name drawn just above it
  top: { x: number; y: number };
}

export interface OutlineOptions {
  // The side of a cell of the grid the field is sampled on
  cellSize: number;
  // How far the field reaches from a member's box
  memberRadius: number;
  // The least room between a member's box and the outline
  margin: number;
  // How far the field reaches from a line of the tree joining the members:
  // the corridor along it is about half as wide
  edgeRadius: number;
  // How far a group not in the cluster pushes the outline away, and how
  // hard
  nonMemberRadius: number;
  nonMemberWeight: number;
  // The level of the field the outline is drawn at; a lone member's box
  // reaches it at full strength
  threshold: number;
  // How far the traced edge may be thinned, in cells: more thins away the
  // grid's small steps, at the cost of as much of the margin
  thinning: number;
  // Rounds of corner cutting before the edge is curved: each round rounds
  // the outline further, and takes a little of the margin at its corners
  smoothing: number;
}

export const OUTLINE_DEFAULTS: OutlineOptions = {
  cellSize: 7,
  memberRadius: 40,
  margin: 10,
  edgeRadius: 32,
  nonMemberRadius: 20,
  nonMemberWeight: 1.5,
  threshold: 0.5,
  thinning: 0.2,
  smoothing: 0,
};

// The strengths the tree's lines are tried at, in turn, before they are
// raised outright
const EDGE_WEIGHTS = [1, 2, 4];
// The longest side of the thinned edge, in cells, before it is curved
const LONGEST_SIDE = 3;
// The points each curve of the outline is sampled at for its extent
const CURVE_SAMPLES = 8;
// How far below the topmost point the outline still counts as its top, so
// that a flat top puts the name over its middle
const TOP_TOLERANCE = 1;

interface Pt {
  x: number;
  y: number;
}

interface Grid {
  x0: number;
  y0: number;
  nx: number;
  ny: number;
  cell: number;
}

// Rounded to a tenth of a pixel, so that the path compares stably; never
// "-0"
const round = (value: number): number => Math.round(value * 10) / 10 + 0;

const segmentDistance = (a: Pt, b: Pt, x: number, y: number): number => {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const length = vx * vx + vy * vy;
  const t = length > 0 ? Math.max(0, Math.min(1, ((x - a.x) * vx + (y - a.y) * vy) / length)) : 0;
  const dx = a.x + t * vx - x;
  const dy = a.y + t * vy - y;
  return Math.sqrt(dx * dx + dy * dy);
};

const centre = (box: Box): Pt => ({ x: box.left + box.width / 2, y: box.top + box.height / 2 });

// The lines of a minimum spanning tree of the members' centres, by Prim's
// method from the first member; of two equal lines the earlier member wins
const spanningTree = (points: Pt[]): [Pt, Pt][] => {
  const count = points.length;
  const joined = new Uint8Array(count);
  const best = new Float64Array(count).fill(Infinity);
  const from = new Int32Array(count).fill(-1);
  const lines: [Pt, Pt][] = [];
  let current = 0;
  joined[0] = 1;
  for (let step = 1; step < count; step += 1) {
    let next = -1;
    for (let index = 0; index < count; index += 1) {
      if (joined[index]) {
        continue;
      }
      const dx = points[index].x - points[current].x;
      const dy = points[index].y - points[current].y;
      const distance = dx * dx + dy * dy;
      if (distance < best[index]) {
        best[index] = distance;
        from[index] = current;
      }
      if (next < 0 || best[index] < best[next]) {
        next = index;
      }
    }
    joined[next] = 1;
    lines.push([points[from[next]], points[next]]);
    current = next;
  }
  return lines;
};

// The columns and rows of the grid's points within a rectangle
interface Span {
  i0: number;
  i1: number;
  j0: number;
  j1: number;
}

const spanOf = (grid: Grid, left: number, top: number, right: number, bottom: number): Span => ({
  i0: Math.max(0, Math.floor((left - grid.x0) / grid.cell)),
  i1: Math.min(grid.nx - 1, Math.ceil((right - grid.x0) / grid.cell)),
  j0: Math.max(0, Math.floor((top - grid.y0) / grid.cell)),
  j1: Math.min(grid.ny - 1, Math.ceil((bottom - grid.y0) / grid.cell)),
});

// The loops below are written out by hand, not through a callback per
// point: they are most of the time an outline takes

// Adds a falloff about a box: full weight on it, nothing at `reach` from it
const addBox = (field: Float64Array, grid: Grid, box: Box, reach: number, weight: number): void => {
  const right = box.left + box.width;
  const bottom = box.top + box.height;
  const span = spanOf(grid, box.left - reach, box.top - reach, right + reach, bottom + reach);
  const most = reach * reach;
  for (let j = span.j0; j <= span.j1; j += 1) {
    const y = grid.y0 + j * grid.cell;
    const dy = Math.max(box.top - y, 0, y - bottom);
    const row = j * grid.nx;
    for (let i = span.i0; i <= span.i1; i += 1) {
      const x = grid.x0 + i * grid.cell;
      const dx = Math.max(box.left - x, 0, x - right);
      const squared = dx * dx + dy * dy;
      if (squared < most) {
        const fall = 1 - Math.sqrt(squared) / reach;
        field[row + i] += weight * fall * fall;
      }
    }
  }
};

// Adds a falloff about a line
const addLine = (field: Float64Array, grid: Grid, a: Pt, b: Pt, reach: number): void => {
  const span = spanOf(
    grid,
    Math.min(a.x, b.x) - reach,
    Math.min(a.y, b.y) - reach,
    Math.max(a.x, b.x) + reach,
    Math.max(a.y, b.y) + reach,
  );
  for (let j = span.j0; j <= span.j1; j += 1) {
    const y = grid.y0 + j * grid.cell;
    const row = j * grid.nx;
    for (let i = span.i0; i <= span.i1; i += 1) {
      const distance = segmentDistance(a, b, grid.x0 + i * grid.cell, y);
      if (distance < reach) {
        const fall = 1 - distance / reach;
        field[row + i] += fall * fall;
      }
    }
  }
};

// Raises the field to a slope that crosses the threshold `room` from a
// box, one unit a cell: the outline then lies no nearer the box than that,
// whatever lowered the field there
const raiseBox = (floor: Float64Array, grid: Grid, threshold: number, room: number, box: Box): void => {
  const band = room + 2 * grid.cell;
  const right = box.left + box.width;
  const bottom = box.top + box.height;
  const span = spanOf(grid, box.left - band, box.top - band, right + band, bottom + band);
  for (let j = span.j0; j <= span.j1; j += 1) {
    const y = grid.y0 + j * grid.cell;
    const dy = Math.max(box.top - y, 0, y - bottom);
    const row = j * grid.nx;
    for (let i = span.i0; i <= span.i1; i += 1) {
      const x = grid.x0 + i * grid.cell;
      const dx = Math.max(box.left - x, 0, x - right);
      const level = threshold + (room - Math.sqrt(dx * dx + dy * dy)) / grid.cell;
      if (level > floor[row + i]) {
        floor[row + i] = level;
      }
    }
  }
};

// The same about a line: a corridor `room` either side of it
const raiseLine = (floor: Float64Array, grid: Grid, threshold: number, room: number, a: Pt, b: Pt): void => {
  const band = room + 2 * grid.cell;
  const span = spanOf(
    grid,
    Math.min(a.x, b.x) - band,
    Math.min(a.y, b.y) - band,
    Math.max(a.x, b.x) + band,
    Math.max(a.y, b.y) + band,
  );
  for (let j = span.j0; j <= span.j1; j += 1) {
    const y = grid.y0 + j * grid.cell;
    const row = j * grid.nx;
    for (let i = span.i0; i <= span.i1; i += 1) {
      const level = threshold + (room - segmentDistance(a, b, grid.x0 + i * grid.cell, y)) / grid.cell;
      if (level > floor[row + i]) {
        floor[row + i] = level;
      }
    }
  }
};

// Whether every member's centre is reached from the first through points
// above the threshold, side by side
const joinedUp = (field: Float64Array, grid: Grid, threshold: number, seeds: number[]): boolean => {
  const seen = new Uint8Array(field.length);
  const stack = new Int32Array(field.length);
  let size = 0;
  stack[size++] = seeds[0];
  seen[seeds[0]] = 1;
  // The rim is outside, so a point reached is never on it and its four
  // neighbours are all on the grid
  const steps = [-1, 1, -grid.nx, grid.nx];
  while (size > 0) {
    const index = stack[--size];
    for (const step of steps) {
      const next = index + step;
      if (!seen[next] && field[next] >= threshold) {
        seen[next] = 1;
        stack[size++] = next;
      }
    }
  }
  return seeds.every((seed) => seen[seed] === 1);
};

// The closed edges of the region above the threshold, by marching squares;
// a crossing is placed on a cell's side by linear interpolation
const contours = (field: Float64Array, grid: Grid, threshold: number): Pt[][] => {
  const { nx, ny, cell, x0, y0 } = grid;
  const across = ny * (nx - 1);
  const total = across + (ny - 1) * nx;
  const first = new Int32Array(total).fill(-1);
  const second = new Int32Array(total).fill(-1);
  const xs = new Float64Array(total);
  const ys = new Float64Array(total);
  // A side's id: the sides across first, row by row, then the sides down
  const point = (id: number): void => {
    if (id < across) {
      const j = Math.floor(id / (nx - 1));
      const i = id % (nx - 1);
      const a = field[j * nx + i];
      const b = field[j * nx + i + 1];
      xs[id] = x0 + (i + (threshold - a) / (b - a)) * cell;
      ys[id] = y0 + j * cell;
    } else {
      const j = Math.floor((id - across) / nx);
      const i = (id - across) % nx;
      const a = field[j * nx + i];
      const b = field[(j + 1) * nx + i];
      xs[id] = x0 + i * cell;
      ys[id] = y0 + (j + (threshold - a) / (b - a)) * cell;
    }
  };
  const join = (from: number, to: number): void => {
    if (first[from] < 0) {
      first[from] = to;
      point(from);
    } else {
      second[from] = to;
    }
  };
  const link = (a: number, b: number): void => {
    join(a, b);
    join(b, a);
  };
  for (let j = 0; j < ny - 1; j += 1) {
    for (let i = 0; i < nx - 1; i += 1) {
      const tl = field[j * nx + i] >= threshold ? 8 : 0;
      const tr = field[j * nx + i + 1] >= threshold ? 4 : 0;
      const br = field[(j + 1) * nx + i + 1] >= threshold ? 2 : 0;
      const bl = field[(j + 1) * nx + i] >= threshold ? 1 : 0;
      const kind = tl | tr | br | bl;
      if (kind === 0 || kind === 15) {
        continue;
      }
      const top = j * (nx - 1) + i;
      const bottom = (j + 1) * (nx - 1) + i;
      const left = across + j * nx + i;
      const right = left + 1;
      const middle =
        (field[j * nx + i] + field[j * nx + i + 1] + field[(j + 1) * nx + i + 1] + field[(j + 1) * nx + i]) / 4 >=
        threshold;
      switch (kind) {
        case 1:
        case 14:
          link(left, bottom);
          break;
        case 2:
        case 13:
          link(bottom, right);
          break;
        case 3:
        case 12:
          link(left, right);
          break;
        case 4:
        case 11:
          link(top, right);
          break;
        case 6:
        case 9:
          link(top, bottom);
          break;
        case 7:
        case 8:
          link(left, top);
          break;
        case 5:
          if (middle) {
            link(left, top);
            link(bottom, right);
          } else {
            link(left, bottom);
            link(top, right);
          }
          break;
        case 10:
          if (middle) {
            link(top, right);
            link(left, bottom);
          } else {
            link(left, top);
            link(bottom, right);
          }
          break;
        default:
          break;
      }
    }
  }
  const done = new Uint8Array(total);
  const loops: Pt[][] = [];
  for (let start = 0; start < total; start += 1) {
    if (first[start] < 0 || done[start]) {
      continue;
    }
    const loop: Pt[] = [];
    let previous = -1;
    let current = start;
    while (current >= 0 && !done[current]) {
      done[current] = 1;
      loop.push({ x: xs[current], y: ys[current] });
      const next = first[current] === previous ? second[current] : first[current];
      previous = current;
      current = next;
    }
    if (loop.length >= 3) {
      loops.push(loop);
    }
  }
  return loops;
};

const area = (loop: Pt[]): number => {
  let sum = 0;
  for (let index = 0; index < loop.length; index += 1) {
    const a = loop[index];
    const b = loop[(index + 1) % loop.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
};

// Douglas–Peucker over an open run of points, its ends kept
const simplifyRun = (points: Pt[], tolerance: number): Pt[] => {
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length > 0) {
    const [from, to] = stack.pop()!;
    let farthest = -1;
    let most = tolerance;
    for (let index = from + 1; index < to; index += 1) {
      const distance = segmentDistance(points[from], points[to], points[index].x, points[index].y);
      if (distance > most) {
        most = distance;
        farthest = index;
      }
    }
    if (farthest >= 0) {
      keep[farthest] = 1;
      stack.push([from, farthest], [farthest, to]);
    }
  }
  return points.filter((_point, index) => keep[index] === 1);
};

// A closed loop thinned: split at the point farthest from its first, each
// half thinned on its own
const simplifyLoop = (loop: Pt[], tolerance: number): Pt[] => {
  let farthest = 0;
  let most = -1;
  loop.forEach((point, index) => {
    const distance = (point.x - loop[0].x) ** 2 + (point.y - loop[0].y) ** 2;
    if (distance > most) {
      most = distance;
      farthest = index;
    }
  });
  const one = simplifyRun(loop.slice(0, farthest + 1), tolerance);
  const two = simplifyRun([...loop.slice(farthest), loop[0]], tolerance);
  const thinned = [...one, ...two.slice(1, -1)];
  return thinned.length >= 3 ? thinned : loop;
};

// A thinned loop with its long sides cut into pieces no longer than
// `longest`, so that a curve's bend at a corner reaches only a short way
// along the side beside it
const divide = (loop: Pt[], longest: number): Pt[] =>
  loop.flatMap((point, index) => {
    const next = loop[(index + 1) % loop.length];
    const parts = Math.ceil(Math.hypot(next.x - point.x, next.y - point.y) / longest);
    return Array.from({ length: Math.max(1, parts) }, (_unused, part) => ({
      x: point.x + ((next.x - point.x) * part) / parts,
      y: point.y + ((next.y - point.y) * part) / parts,
    }));
  });

// A closed loop with its corners cut `rounds` times (Chaikin): each side
// gives way to the two points a quarter and three quarters along it. The
// first point stays first, so that the loop still begins near its top.
const rounded = (loop: Pt[], rounds: number): Pt[] => {
  let points = loop;
  for (let round = 0; round < rounds && points.length >= 3; round += 1) {
    points = points.flatMap((point, index) => {
      const next = points[(index + 1) % points.length];
      return [
        { x: point.x * 0.75 + next.x * 0.25, y: point.y * 0.75 + next.y * 0.25 },
        { x: point.x * 0.25 + next.x * 0.75, y: point.y * 0.25 + next.y * 0.75 },
      ];
    });
  }
  return points;
};

// The direction of the curve at a point: along the line from the point
// before it to the point after
const tangent = (before: Pt, after: Pt): Pt => {
  const length = Math.hypot(after.x - before.x, after.y - before.y) || 1;
  return { x: (after.x - before.x) / length, y: (after.y - before.y) / length };
};

// The loop as curves through its points, each one cubic Bézier: the start,
// two controls and the end. Each point's direction is the Catmull–Rom one,
// its controls a third of its side away, so that an uneven side never
// overshoots.
const curves = (loop: Pt[]): [Pt, Pt, Pt, Pt][] =>
  loop.map((point, index) => {
    const before = loop[(index - 1 + loop.length) % loop.length];
    const next = loop[(index + 1) % loop.length];
    const after = loop[(index + 2) % loop.length];
    const reach = Math.hypot(next.x - point.x, next.y - point.y) / 3;
    const leaving = tangent(before, next);
    const arriving = tangent(point, after);
    return [
      point,
      { x: point.x + leaving.x * reach, y: point.y + leaving.y * reach },
      { x: next.x - arriving.x * reach, y: next.y - arriving.y * reach },
      next,
    ];
  });

const bezier = (curve: [Pt, Pt, Pt, Pt], t: number): Pt => {
  const u = 1 - t;
  const [a, b, c, d] = curve;
  return {
    x: u * u * u * a.x + 3 * u * u * t * b.x + 3 * u * t * t * c.x + t * t * t * d.x,
    y: u * u * u * a.y + 3 * u * u * t * b.y + 3 * u * t * t * c.y + t * t * t * d.y,
  };
};

// The outline of a cluster round its members' boxes, kept off the boxes of
// the groups not in it; null for no members
export const outlineOf = (
  members: Box[],
  others: Box[],
  options: Partial<OutlineOptions> = {},
): Outline | null => {
  if (members.length === 0) {
    return null;
  }
  const settings = { ...OUTLINE_DEFAULTS, ...options };
  const { cellSize: cell, threshold } = settings;
  // The members' boxes are held this far inside: the margin and half a
  // cell, which the thinning and the smoothing may take back
  const room = settings.margin + cell / 2;
  const pad = Math.max(settings.memberRadius, room + 2 * cell) + 2 * cell;
  const left = Math.min(...members.map((box) => box.left)) - pad;
  const top = Math.min(...members.map((box) => box.top)) - pad;
  const right = Math.max(...members.map((box) => box.left + box.width)) + pad;
  const bottom = Math.max(...members.map((box) => box.top + box.height)) + pad;
  const grid: Grid = {
    x0: left,
    y0: top,
    nx: Math.ceil((right - left) / cell) + 1,
    ny: Math.ceil((bottom - top) / cell) + 1,
    cell,
  };
  const size = grid.nx * grid.ny;

  const base = new Float64Array(size);
  for (const box of members) {
    addBox(base, grid, box, settings.memberRadius, 1);
  }
  const near = others.filter(
    (box) => box.left < right && box.left + box.width > left && box.top < bottom && box.top + box.height > top,
  );
  for (const box of near) {
    addBox(base, grid, box, settings.nonMemberRadius, -settings.nonMemberWeight);
  }
  const centres = members.map(centre);
  const lines = spanningTree(centres);
  const along = new Float64Array(size);
  for (const [a, b] of lines) {
    addLine(along, grid, a, b, settings.edgeRadius);
  }
  const floor = new Float64Array(size).fill(-Infinity);
  for (const box of members) {
    raiseBox(floor, grid, threshold, room, box);
  }
  const seeds = centres.map(
    (point) =>
      Math.round((point.y - grid.y0) / cell) * grid.nx + Math.round((point.x - grid.x0) / cell),
  );

  const field = new Float64Array(size);
  const fill = (weight: number): void => {
    for (let index = 0; index < size; index += 1) {
      field[index] = Math.max(base[index] + weight * along[index], floor[index]);
    }
    // The grid's rim is outside, so that every edge closes
    for (let i = 0; i < grid.nx; i += 1) {
      field[i] = -1;
      field[size - 1 - i] = -1;
    }
    for (let j = 0; j < grid.ny; j += 1) {
      field[j * grid.nx] = -1;
      field[j * grid.nx + grid.nx - 1] = -1;
    }
  };
  let joined = false;
  for (const weight of EDGE_WEIGHTS) {
    fill(weight);
    if (joinedUp(field, grid, threshold, seeds)) {
      joined = true;
      break;
    }
  }
  if (!joined) {
    // The lines alone do not join the members: a corridor is raised along
    // them outright, at least a cell either side so the grid holds it
    const corridor = Math.max(settings.edgeRadius / 4, cell);
    for (const [a, b] of lines) {
      raiseLine(floor, grid, threshold, corridor, a, b);
    }
    fill(EDGE_WEIGHTS[EDGE_WEIGHTS.length - 1]);
  }

  // The outer edge of the region: the loop of most area, holes round
  // groups not in the cluster left out
  const loops = contours(field, grid, threshold);
  if (loops.length === 0) {
    return null;
  }
  let outer = loops[0];
  let most = Math.abs(area(outer));
  for (const loop of loops.slice(1)) {
    const extent = Math.abs(area(loop));
    if (extent > most) {
      most = extent;
      outer = loop;
    }
  }
  // Begun at its topmost point, the leftmost of those
  let start = 0;
  outer.forEach((point, index) => {
    const at = outer[start];
    if (point.y < at.y || (point.y === at.y && point.x < at.x)) {
      start = index;
    }
  });
  const thinned = divide(
    rounded(
      simplifyLoop([...outer.slice(start), ...outer.slice(0, start)], cell * settings.thinning),
      settings.smoothing,
    ),
    cell * LONGEST_SIDE,
  ).map((point) => ({ x: round(point.x), y: round(point.y) }));
  return outlineThrough(thinned);
};

// The outline curved through a loop of points, begun at its first: the
// path, and its extent and top measured on the curves
const outlineThrough = (thinned: Pt[]): Outline => {
  const pieces = curves(thinned).map(
    ([a, b, c, d]): [Pt, Pt, Pt, Pt] => [a, { x: round(b.x), y: round(b.y) }, { x: round(c.x), y: round(c.y) }, d],
  );

  const path = [
    `M ${thinned[0].x} ${thinned[0].y}`,
    ...pieces.map(([, b, c, d]) => `C ${b.x} ${b.y} ${c.x} ${c.y} ${d.x} ${d.y}`),
    "Z",
  ].join(" ");
  const samples = pieces.flatMap((piece) =>
    Array.from({ length: CURVE_SAMPLES }, (_unused, step) => bezier(piece, step / CURVE_SAMPLES)),
  );
  const highest = Math.min(...samples.map((point) => point.y));
  const atTop = samples.filter((point) => point.y <= highest + TOP_TOLERANCE).map((point) => point.x);
  return {
    path,
    bounds: {
      left: round(Math.min(...samples.map((point) => point.x))),
      top: round(highest),
      right: round(Math.max(...samples.map((point) => point.x))),
      bottom: round(Math.max(...samples.map((point) => point.y))),
    },
    top: { x: round((Math.min(...atTop) + Math.max(...atTop)) / 2), y: round(highest) },
  };
};

// An outline's path as a polygon: each curve sampled finely, each line taken
// whole
// The angles the rounded outline is measured at, round its middle
const ROUND_STEPS = 96;
// How many waves round the turn the radii keep: two give an ellipse, leaning
// to one side where the groups do; more would let the outline wobble
const ROUND_HARMONICS = 2;
// How much of their waves the radii keep each time the outline is found not
// convex, and how often at most before it is drawn as a circle
const ROUND_DAMPING = 0.8;
const ROUND_TRIES = 12;
// How far either side of a shared group its bulge falls away, as an angle
// seen from the cluster's middle: wider is softer, but reaches nearer the
// groups beside it. The narrower ones are tried where the softest would
// hold a group of another cluster.
export const BULGE_FALLS: readonly number[] = [0.5, 0.3, 0.16];
const ROUND_BULGE_FALL = BULGE_FALLS[0];
// How sharply bulges that meet blend: higher keeps each nearer its own height
const ROUND_BULGE_BLEND = 4;

// The convex hull of points (Andrew's monotone chain), anticlockwise
const hull = (points: Pt[]): Pt[] => {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  if (sorted.length < 3) {
    return sorted;
  }
  const cross = (o: Pt, a: Pt, b: Pt) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Pt[] = [];
  for (const point of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) {
      lower.pop();
    }
    lower.push(point);
  }
  const upper: Pt[] = [];
  for (const point of [...sorted].reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) {
      upper.pop();
    }
    upper.push(point);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
};

// How far from a middle inside a convex loop its edge lies in a direction
const reachOf = (loop: Pt[], middle: Pt, angle: number): number => {
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  let reach = 0;
  loop.forEach((point, index) => {
    const next = loop[(index + 1) % loop.length];
    const ex = next.x - point.x;
    const ey = next.y - point.y;
    const denominator = dx * ey - dy * ex;
    if (Math.abs(denominator) < 1e-9) {
      return;
    }
    const along = ((point.x - middle.x) * ey - (point.y - middle.y) * ex) / denominator;
    const share = ((point.x - middle.x) * dy - (point.y - middle.y) * dx) / denominator;
    if (along > 0 && share >= -1e-9 && share <= 1 + 1e-9) {
      reach = Math.max(reach, along);
    }
  });
  return reach;
};

// Radii measured at even angles round the turn, kept to their slowest waves:
// the mean and the first `harmonics` waves, each of those scaled by `keep`
const lowPass = (radii: number[], harmonics: number, keep: number): number[] => {
  const count = radii.length;
  const mean = radii.reduce((sum, radius) => sum + radius, 0) / count;
  const waves = Array.from({ length: harmonics }, (_unused, wave) => {
    let cos = 0;
    let sin = 0;
    radii.forEach((radius, index) => {
      const angle = (2 * Math.PI * (wave + 1) * index) / count;
      cos += radius * Math.cos(angle);
      sin += radius * Math.sin(angle);
    });
    return { cos: (2 * cos) / count, sin: (2 * sin) / count };
  });
  return radii.map((_unused, index) =>
    waves.reduce((radius, { cos, sin }, wave) => {
      const angle = (2 * Math.PI * (wave + 1) * index) / count;
      return radius + keep * (cos * Math.cos(angle) + sin * Math.sin(angle));
    }, mean),
  );
};

// Whether a loop of points turns the same way at every point
const convexLoop = (points: Pt[]): boolean => {
  const turns = points.map((point, index) => {
    const next = points[(index + 1) % points.length];
    const after = points[(index + 2) % points.length];
    return (next.x - point.x) * (after.y - next.y) - (next.y - point.y) * (after.x - next.x);
  });
  return turns.every((turn) => turn >= 0) || turns.every((turn) => turn <= 0);
};

// A box's corners, the box grown by a margin all round
const grownCorners = (box: Box, margin: number): Pt[] => [
  { x: box.left - margin, y: box.top - margin },
  { x: box.left + box.width + margin, y: box.top - margin },
  { x: box.left + box.width + margin, y: box.top + box.height + margin },
  { x: box.left - margin, y: box.top + box.height + margin },
];

// An angle brought into the half turn either side of nothing
const wrapped = (angle: number): number => Math.atan2(Math.sin(angle), Math.cos(angle));

// A cluster's outline as radii round its middle, at even angles from
// straight up: quick to ask whether a point lies inside, and drawn through
// its points as one smooth curve
export interface RoundShape {
  middle: Pt;
  // The angle of each radius, the first straight up
  angles: number[];
  radii: number[];
  // How far each radius is swollen by bulges out to shared groups
  swell: number[];
  // The margin the shape keeps round each group, for bulges added later
  margin: number;
}

// A cluster's shape as a near ellipse round its own groups: the hull of
// their boxes, each grown by the margin, measured from its middle all
// round, the radii kept to their slowest waves so the curve leans toward a
// group off to one side without ever wobbling, and grown last just so far
// that no box's corner lies outside. Convex and smooth; null for no groups.
export const roundBaseOf = (members: Box[], margin = OUTLINE_DEFAULTS.margin): RoundShape | null => {
  if (members.length === 0) {
    return null;
  }
  const corners = members.flatMap((box) => grownCorners(box, margin));
  const loop = hull(corners);
  const xs = loop.map((point) => point.x);
  const ys = loop.map((point) => point.y);
  const middle = {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
  const angles = Array.from({ length: ROUND_STEPS }, (_unused, step) => (step / ROUND_STEPS) * 2 * Math.PI - Math.PI / 2);
  const reach = angles.map((angle) => reachOf(loop, middle, angle));
  // The radii grown just so far that every own group is held
  const grownRadii = (radii: number[]): number[] => {
    const grown = Math.max(1, ...reach.map((radius, index) => radius / radii[index]));
    return radii.map((radius) => radius * grown);
  };
  const pointsOf = (radii: number[]): Pt[] =>
    angles.map((angle, index) => ({
      x: middle.x + Math.cos(angle) * radii[index],
      y: middle.y + Math.sin(angle) * radii[index],
    }));
  // The waves damped until the outline is convex: a group far off to one
  // side would otherwise dent the far side in
  let keep = 1;
  let radii = grownRadii(lowPass(reach, ROUND_HARMONICS, keep));
  for (let tries = 0; !convexLoop(pointsOf(radii)) && tries < ROUND_TRIES; tries += 1) {
    keep *= ROUND_DAMPING;
    radii = grownRadii(lowPass(reach, ROUND_HARMONICS, keep));
  }
  if (!convexLoop(pointsOf(radii))) {
    radii = grownRadii(lowPass(reach, ROUND_HARMONICS, 0));
  }
  return { middle, angles, radii, swell: angles.map(() => 0), margin };
};

// A shape bulging out to each shared group it does not already hold: each
// bulge flat across its group, falling softly away either side, so that the
// rest of the outline keeps to the cluster's own groups and does not swell
// over its neighbours'. Bulges that meet blend smoothly into one, never
// lower than either; a shared group lies inside every cluster it is in.
// Each bulge falls away over its own angle in `falls`, by default the
// softest.
export const bulgedRound = (
  base: RoundShape,
  shared: Box[],
  falls: readonly number[] = [],
): RoundShape => {
  const { middle, angles, radii, margin } = base;
  const bulges = shared.flatMap((box, index) => {
    const fall = falls[index] ?? ROUND_BULGE_FALL;
    const grown = grownCorners(box, margin);
    const need = angles.map((angle) => reachOf(grown, middle, angle));
    const height = Math.max(0, ...need.map((radius, index) => radius - radii[index]));
    if (height <= 0) {
      return [];
    }
    // The turn the grown box takes up, seen from the middle
    const toward = Math.atan2(box.top + box.height / 2 - middle.y, box.left + box.width / 2 - middle.x);
    const offsets = grown.map((corner) => wrapped(Math.atan2(corner.y - middle.y, corner.x - middle.x) - toward));
    const from = Math.min(...offsets);
    const to = Math.max(...offsets);
    return [
      angles.map((angle) => {
        const offset = wrapped(angle - toward);
        const beyond = offset < from ? from - offset : offset > to ? offset - to : 0;
        return beyond >= fall ? 0 : (height * (1 + Math.cos((Math.PI * beyond) / fall))) / 2;
      }),
    ];
  });
  const swell = angles.map((_unused, index) =>
    bulges.length === 0
      ? 0
      : Math.pow(
          bulges.reduce((sum, bulge) => sum + Math.pow(bulge[index], ROUND_BULGE_BLEND), 0),
          1 / ROUND_BULGE_BLEND,
        ),
  );
  return { ...base, swell };
};

// Whether a point lies inside a shape: its distance from the middle against
// the radius at its angle, read between the two nearest radii
export const insideRound = (shape: RoundShape, point: Pt): boolean => {
  const { middle, radii, swell } = shape;
  const count = radii.length;
  const distance = Math.hypot(point.x - middle.x, point.y - middle.y);
  const turn = Math.atan2(point.y - middle.y, point.x - middle.x) + Math.PI / 2;
  const at = (((turn / (2 * Math.PI)) * count) % count + count) % count;
  const below = Math.floor(at) % count;
  const above = (below + 1) % count;
  const share = at - Math.floor(at);
  const radius =
    (radii[below] + swell[below]) * (1 - share) + (radii[above] + swell[above]) * share;
  return distance < radius;
};

// Whether any of a box's corners, the middles of its sides or its middle,
// the box grown by `clear`, lies inside a shape
export const touchesRound = (shape: RoundShape, box: Box, clear = 0): boolean => {
  const left = box.left - clear;
  const top = box.top - clear;
  const right = box.left + box.width + clear;
  const bottom = box.top + box.height + clear;
  const middleX = (left + right) / 2;
  const middleY = (top + bottom) / 2;
  return [
    { x: left, y: top },
    { x: middleX, y: top },
    { x: right, y: top },
    { x: right, y: middleY },
    { x: right, y: bottom },
    { x: middleX, y: bottom },
    { x: left, y: bottom },
    { x: left, y: middleY },
    { x: middleX, y: middleY },
  ].some((point) => insideRound(shape, point));
};

// A shape drawn: the curve through its points, begun at the top. The top,
// for the name, is the highest point outside any bulge.
export const roundOutline = (shape: RoundShape): Outline => {
  const { middle, angles, radii, swell } = shape;
  const points = angles.map((angle, index) => ({
    x: round(middle.x + Math.cos(angle) * (radii[index] + swell[index])),
    y: round(middle.y + Math.sin(angle) * (radii[index] + swell[index])),
  }));
  const outline = outlineThrough(points);
  const plain = points.filter((_unused, index) => swell[index] < 0.5);
  if (swell.some((value) => value >= 0.5) && plain.length > 0) {
    const highest = plain.reduce((best, point) => (point.y < best.y ? point : best));
    outline.top = { x: highest.x, y: highest.y };
  }
  return outline;
};

// A cluster's outline drawn as a near ellipse round its own groups
// (roundBaseOf), bulging out to the groups it shares with other clusters,
// given apart (bulgedRound). Smooth, and always holding every group. A
// cluster of shared groups alone is an ellipse round them.
export const roundOutlineOf = (
  members: Box[],
  margin = OUTLINE_DEFAULTS.margin,
  shared: Box[] = [],
): Outline | null => {
  const base = roundBaseOf(members.length > 0 ? members : shared, margin);
  if (!base) {
    return null;
  }
  return roundOutline(members.length > 0 ? bulgedRound(base, shared) : base);
};

export const pathPolygon = (path: string): Pt[] => {
  const tokens = path.trim().split(/\s+/);
  const points: Pt[] = [];
  let at: Pt = { x: 0, y: 0 };
  let index = 0;
  const number = (): number => Number(tokens[index++]);
  while (index < tokens.length) {
    const command = tokens[index++];
    if (command === "M" || command === "L") {
      at = { x: number(), y: number() };
      points.push(at);
    } else if (command === "C") {
      const b = { x: number(), y: number() };
      const c = { x: number(), y: number() };
      const d = { x: number(), y: number() };
      for (let step = 1; step <= 16; step += 1) {
        points.push(bezier([at, b, c, d], step / 16));
      }
      at = d;
    } else if (command !== "Z") {
      throw new Error(`unexpected path command ${command}`);
    }
  }
  return points;
};

// Whether a point lies inside a polygon, by ray casting
export const insidePolygon = (polygon: Pt[], point: Pt): boolean => {
  let within = false;
  for (let index = 0, before = polygon.length - 1; index < polygon.length; before = index++) {
    const a = polygon[index];
    const b = polygon[before];
    if (a.y > point.y !== b.y > point.y && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x) {
      within = !within;
    }
  }
  return within;
};
